import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "reactstrap";
import ReactPaginate from "react-paginate";
import { format } from "date-fns";
import { ProductContext } from "../../../../store";
import ReportHeader from "../../../../components/Search/ReportHeader";
import ReportSortArrows from "../../../../components/Search/ReportSortArrows";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import TableSkeleton from "../../../../components/Skeleton/TableSkeleton";
import { PostData } from "../../../../services/FetchData";
import { searchZeroReportsAnalyticsUrl } from "../../../../constants/searchReportsAnalyticsApi";
import { approveOrRejectSynonyms } from "../../../../constants/aiSynonymsApi";
import { toast } from "react-toastify";
import AIRecommendationPopover from "../../../../components/Search/AIRecommendationPopover";

const ZeroResultQueries = () => {
  const navigate = useNavigate();
  const [pageLoader, setPageLoader] = useState(true),
    [tableLoader, setTableLoader] = useState(true),
    [data, setData] = useState([]),
    [totalPageCount, setTotalPageCount] = useState(0),
    [limit, setLimit] = useState(25),
    [page, setPage] = useState(0),
    [sortField, setSortField] = useState(null),
    [sortOrder, setSortOrder] = useState("desc"),
    [openAiPopoverForQuery, setOpenAiPopoverForQuery] = useState(null),
    [approvingQuery, setApprovingQuery] = useState(null),
    { dateRange, setDateRange, currentWebsiteId } = useContext(ProductContext);

  useEffect(() => {
    if (currentWebsiteId) {
      getReports();
    }
  }, [dateRange, limit, page, currentWebsiteId, sortField, sortOrder]);

  const mapSortFieldToApi = (field) => {
    const fieldMap = {
      query: "searchQuery",
      volume: "searchVolume",
      zeroResultSession: "zeroResultPercentage",
    };
    return fieldMap[field] || field;
  };

  const setSort = (field, order, e) => {
    e?.stopPropagation?.();
    setPage(0);
    setSortField(field);
    setSortOrder(order);
  };

  const toggleSort = (field, e) => {
    e?.stopPropagation?.();
    setPage(0);
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const getReports = () => {
    setTableLoader(true);

    const payload = {
      websiteId: currentWebsiteId,
      startDate: format(dateRange.startDate, "yyyy-MM-dd"),
      endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      page: page + 1,
      limit,
      sortField: sortField ? mapSortFieldToApi(sortField) : "",
      sortOrder: sortOrder || "desc",
    };

    PostData("ms1", searchZeroReportsAnalyticsUrl("searchZeroResult"), payload)
      .then((response) => {
        if (response === "Invalid" || response === "undefined" || !response) {
          setData([]);
          return;
        }

        if (response.status !== "success" || !response.data) {
          toast.error(response.message || "Failed to fetch data");
          setData([]);
          return;
        }

        const dataArray = Array.isArray(response.data.data) ? response.data.data : [];

        const transformedData = dataArray.map((item) => ({
          query: item.searchQuery || "",
          volume: item.searchVolume || 0,
          zeroResultSession:
            typeof item.zeroResultPercentage === "number"
              ? `${item.zeroResultPercentage.toFixed(2)}%`
              : "0.00%",
          synonymsId: item.synonymsId || null,
          aiTexts: Array.isArray(item.aiTexts) ? item.aiTexts : [],
          isApproved: !!item.isApproved,
          userFriendlyMessage: item.userFriendlyMessage || "",
        }));

        setData(transformedData);
        const totalRecord = response.data.total || 0;
        const totalPage = Math.ceil(totalRecord / limit);
        setTotalPageCount(totalPage);
      })
      .catch((err) => {
        console.error("Error fetching zero result queries:", err);
      })
      .finally(() => {
        setTableLoader(false);
        setPageLoader(false);
      });
  };

  const approveSynonyms = async (query) => {
    const targetRow = data.find((row) => row.query === query);
    if (!targetRow?.synonymsId) {
      toast.error("Synonym id not found for this query.");
      return;
    }

    const action = targetRow.isApproved ? "reject" : "approve";
    setApprovingQuery(query);

    try {
      const response = await approveOrRejectSynonyms({
        websiteId: currentWebsiteId,
        synonymsId: targetRow.synonymsId,
        searchTerm: targetRow.query,
        synonyms: targetRow.aiTexts,
        action,
      });

      if (response?.status === "success") {
        setData((prev) =>
          prev.map((row) =>
            row.query === query
              ? { ...row, isApproved: action === "approve" }
              : row,
          ),
        );
        toast.success(
          action === "approve"
            ? "Synonym approved successfully."
            : "Synonym rejected successfully.",
        );
      } else {
        toast.error(response?.message || "Failed to update approval.");
      }
    } catch (err) {
      console.error("Error updating synonym approval:", err);
      toast.error("Failed to update approval.");
    }

    setApprovingQuery(null);
  };

  const toggleAiPopover = (query) => {
    console.log("Toggling AI popover for query:", query);
    setOpenAiPopoverForQuery((prev) => (prev === query ? null : query));
  };

  const handlePreviewProducts = (data) => {
    navigate("/search/reports/ai-synonym-preview", {
      state:data,
    });
  };

  return (
    <div>
      {pageLoader ? (
        <DetailSkeleton />
      ) : (
        <React.Fragment>
          <ReportHeader
            range={dateRange}
            onDateChange={(range) => setDateRange(range)}
            reportKey="zeroResultQueries"
            sortField={sortField}
            sortOrder={sortOrder}
          />
          <div className="tab-content mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Zero-Result Queries Report</h4>
              <div className="d-flex align-items-center">
                <label htmlFor="limit" className="me-2">
                  Items Per Page:
                </label>
                <select
                  id="limit"
                  className="form-select"
                  value={limit}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  style={{ width: "100px", fontSize: "12px" }}
                >
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div>
              {tableLoader ? (
                <TableSkeleton />
              ) : (
                <Table
                  className={`table ${data.length > 0 ? "table-striped" : ""} mt-3 custom-bordered-table`}
                >
                  <thead>
                    <tr>
                      <th
                        className="sortable-header"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => toggleSort("query", e)}
                      >
                        <div className="sortable-header-inner">
                          Search Query
                          <ReportSortArrows
                            field="query"
                            activeSortField={sortField}
                            sortOrder={sortOrder}
                            onSort={setSort}
                          />
                        </div>
                      </th>
                      <th
                        className="sortable-header"
                        style={{ cursor: "pointer" }}
                        onClick={(e) => toggleSort("volume", e)}
                      >
                        <div className="sortable-header-inner">
                          Search Volume
                          <ReportSortArrows
                            field="volume"
                            activeSortField={sortField}
                            sortOrder={sortOrder}
                            onSort={setSort}
                          />
                        </div>
                      </th>
                      <th>AI Suggestions</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="emptyPlaceholder">
                            <div>
                              <div>
                                <img
                                  src={require("./../../../../assets/images/paper-plane-grey.png")}
                                  className="img-fluid"
                                  alt="No data found"
                                />
                              </div>
                              <div className="rsInfoMsg disabled">
                                <div className="flex-1">No records found.</div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      data.map((row, index) => (
                        <tr key={index}>
                          <td>{row.query}</td>
                          <td>{row.volume?.toLocaleString() || 0}</td>
                          <td>
                            <AIRecommendationPopover
                              data={row}
                              isOpen={openAiPopoverForQuery === row.query}
                              onToggle={() => toggleAiPopover(row.query)}
                              onClose={() => setOpenAiPopoverForQuery(null)}
                              onPreview={handlePreviewProducts}
                            />
                          </td>
                          <td>
                            {row?.aiTexts.length > 0 && (
                              <button
                                type="button"
                                className={`btn btn-sm ${row.isApproved ? "btn-danger" : "btn-success"}`}
                                onClick={() => approveSynonyms(row.query)}
                                disabled={approvingQuery === row.query}
                              >
                                {approvingQuery === row.query
                                  ? "Saving..."
                                  : row.isApproved
                                    ? "Reject"
                                    : "Approve"}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              )}
              {totalPageCount > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <ReactPaginate
                    nextLabel=">"
                    onPageChange={(event) => setPage(event.selected)}
                    page={page}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={totalPageCount}
                    previousLabel="<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                  />
                </div>
              )}
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default ZeroResultQueries;
